import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../ui/Button";
import type { Role } from "./roles";

/** The roles to choose from, each with its meaning in one line (access-control §3, WEB-MEM-02). */
export function RoleChoices({
  roles,
  value,
  onChange,
}: {
  roles: Role[];
  value: Role;
  onChange: (role: Role) => void;
}) {
  const { t } = useTranslation();
  const name = useId();
  return (
    <fieldset>
      <legend className="text-sm font-medium text-gray-900">{t("organisation.role.label")}</legend>
      <div className="mt-2 space-y-2">
        {roles.map((r) => (
          <label key={r} className="flex items-start gap-2 text-sm text-gray-900">
            <input
              type="radio"
              name={name}
              value={r}
              checked={value === r}
              onChange={() => onChange(r)}
              className="mt-0.5"
            />
            <span>
              <span className="font-medium">{t(`organisation.role.${r}`)}</span>
              <span className="block text-gray-700">{t(`organisation.roleMeaning.${r}`)}</span>
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/** A form in a modal dialog with Cancel, a submit button and the refusal in words; it stays open until it is closed. */
export function FormDialog({
  open,
  title,
  submitLabel,
  busy,
  error,
  ready = true,
  onSubmit,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  submitLabel: string;
  busy?: boolean;
  error?: string;
  ready?: boolean;
  onSubmit: () => void;
  onClose: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const dialog = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const d = dialog.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    else if (!open && d.open) d.close();
  }, [open]);

  function submit(e: FormEvent) {
    e.preventDefault();
    if (ready && !busy) onSubmit();
  }

  return (
    <dialog
      ref={dialog}
      onClose={onClose}
      aria-labelledby={titleId}
      className="m-auto w-full max-w-md rounded-lg p-0 shadow-xl backdrop:bg-black/40"
    >
      <form onSubmit={submit} className="space-y-4 p-6">
        <h2 id={titleId} className="text-lg font-semibold text-gray-900">
          {title}
        </h2>
        {children}
        {error && (
          <p role="alert" className="text-sm text-red-900">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={busy || !ready}>
            {submitLabel}
          </Button>
        </div>
      </form>
    </dialog>
  );
}

/** Change a member's role (WEB-MEM-02); key it by the member. */
export function RoleDialog({
  open,
  name,
  current,
  roles,
  busy,
  error,
  onSubmit,
  onClose,
}: {
  open: boolean;
  name: string;
  current: Role;
  roles: Role[];
  busy?: boolean;
  error?: string;
  onSubmit: (role: Role) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  // Mounted again for each member (the caller keys it), so it starts from their role.
  const [role, setRole] = useState(current);
  return (
    <FormDialog
      open={open}
      title={t("organisation.members.roleTitle", { name })}
      submitLabel={t("organisation.members.saveRole")}
      busy={busy}
      error={error}
      ready={role !== current}
      onSubmit={() => onSubmit(role)}
      onClose={onClose}
    >
      <RoleChoices roles={roles} value={role} onChange={setRole} />
    </FormDialog>
  );
}
